import { Autocomplete, Avatar, Checkbox, CircularProgress, TextField } from "@mui/material";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import { PortalType } from "contexts/portal.context";
import { Body, Heading2, SmallBody } from "helpers/TypographyComponents";
import { getInitials } from "helpers/utils";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { EligibleAgreementResource } from "store/agreementsStore";
import { fetchEligibleResourcesForAgreement } from "store/agreementsStore";
import { useAppDispatch } from "store/store";

import styles from "./style.module.scss";

interface IPickResourceStep {
    documentType: "agreement" | "avenant" | "framework_contract";
    /**
     * When true, forces the fetch to list framework contracts regardless of `documentType`.
     * Used by the avenant flow when the user targets a framework contract.
     */
    fetchAsFrameworkContract?: boolean;
    onClose: () => void;
    /**
     * Fires immediately when the user picks/clears a resource.
     * Used to update the modal subtitle in real-time.
     */
    onPick: (resource: EligibleAgreementResource | null) => void;
    /**
     * Fires when the user confirms and clicks Continue.
     */
    onContinue: () => void;
    /**
     * When provided, a "Précédent" button appears alongside "Annuler".
     * Used to go back to a preceding step (e.g. the avenant target picker).
     */
    onPrevious?: () => void;
    /** Other beneficiaries of the picked title (excluding the picked one). */
    otherBeneficiariesCount?: number;
    /** Current multi-signature choice — bound to a parent state. */
    multiSignature?: boolean;
    /** Called when the user toggles the multi-signature checkbox. */
    onToggleMultiSignature?: (multi: boolean) => void;
}

const PickResourceStep = ({
    documentType,
    fetchAsFrameworkContract,
    onClose,
    onPick,
    onContinue,
    onPrevious,
    otherBeneficiariesCount = 0,
    multiSignature = false,
    onToggleMultiSignature,
}: IPickResourceStep) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState<EligibleAgreementResource[]>([]);
    const [selected, setSelected] = useState<EligibleAgreementResource | null>(null);
    const [loading, setLoading] = useState(false);

    const effectiveDocumentType = fetchAsFrameworkContract ? "framework_contract" : documentType;
    const isFrameworkContract = effectiveDocumentType === "framework_contract";

    useEffect(() => {
        let cancelled = false;
        const handle = setTimeout(async () => {
            setLoading(true);
            const res = await dispatch(
                fetchEligibleResourcesForAgreement({
                    documentType: effectiveDocumentType,
                    search: inputValue || undefined,
                }),
            );
            if (cancelled) return;
            if (fetchEligibleResourcesForAgreement.fulfilled.match(res)) setOptions(res.payload);
            setLoading(false);
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [inputValue, effectiveDocumentType, dispatch]);

    const title = useMemo(() => {
        if (isFrameworkContract) {
            return documentType === "avenant"
                ? t("ContractSettings.PickResourceForFrameworkContractAvenant")
                : t("ContractSettings.PickResourceForFrameworkContract");
        }
        return documentType === "avenant"
            ? t(`ContractSettings.PickResourceForAvenant_${portalType}`)
            : t(`ContractSettings.PickResourceForAgreement_${portalType}`);
    }, [documentType, isFrameworkContract, portalType, t]);

    const hint = isFrameworkContract
        ? t("ContractSettings.PickResourceHintFrameworkContract")
        : t(`ContractSettings.PickResourceHint_${portalType}`);

    const placeholder = isFrameworkContract
        ? (t("ContractSettings.PickResourcePlaceholderFrameworkContract") as string)
        : (t(`ContractSettings.PickResourcePlaceholder_${portalType}`) as string);

    return (
        <div className={styles["pick-resource-step"]}>
            <div className={styles["pick-resource-step-wrapper"]}>
                <Heading2>{title}</Heading2>
                <Body color="info">{hint}</Body>
                <div className={styles["pick-resource-step-body"]}>
                    <Autocomplete
                        fullWidth
                        options={options}
                        value={selected}
                        onChange={(_, next) => {
                            setSelected(next);
                            onPick(next);
                            onToggleMultiSignature?.(false);
                        }}
                        onInputChange={(_, value) => setInputValue(value)}
                        loading={loading}
                        filterOptions={(x) => x}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(a, b) => a.type === b.type && a.id === b.id}
                        noOptionsText={t("ContractSettings.NoEligibleResource")}
                        renderOption={(props, option) => (
                            <li {...props} key={`${option.type}-${option.id}`}>
                                <div className={styles["pick-resource-option"]}>
                                    <Avatar
                                        src={option.titleAvatarUrl ?? undefined}
                                        alt={option.name}
                                        sx={{ width: 32, height: 32, fontSize: 12 }}
                                    >
                                        {getInitials(option.name)}
                                    </Avatar>
                                    <div className={styles["pick-resource-option-text"]}>
                                        <Body>{option.name}</Body>
                                        <SmallBody color="grey">{option.beneficiaryName}</SmallBody>
                                    </div>
                                </div>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={placeholder}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading && <CircularProgress size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    {selected && !isFrameworkContract && otherBeneficiariesCount > 0 && onToggleMultiSignature && (
                        <div className={styles["pick-resource-step-multi"]}>
                            <MessageBlock
                                type="info"
                                small
                                text={t("ContractSettings.OtherBeneficiariesFound", {
                                    count: otherBeneficiariesCount,
                                })}
                            />
                            <label className={styles["pick-resource-step-multi-toggle"]}>
                                <Checkbox
                                    size="small"
                                    checked={multiSignature}
                                    onChange={(e) => onToggleMultiSignature(e.target.checked)}
                                />
                                <SmallBody>{t("ContractSettings.EnableMultiSignatureToggle")}</SmallBody>
                            </label>
                        </div>
                    )}
                </div>
            </div>
            <div className={styles["pick-resource-step-actions"]}>
                <div style={{ display: "flex", gap: 8 }}>
                    <StyledButton buttonType="secondary" onClick={onClose}>
                        {t("Cancel")}
                    </StyledButton>
                    {onPrevious && (
                        <StyledButton buttonType="secondary" onClick={onPrevious}>
                            {t("NewElement.Previous")}
                        </StyledButton>
                    )}
                </div>
                <StyledButton buttonType="primary" onClick={() => selected && onContinue()} disabled={!selected}>
                    {t("NewElement.Continue")}
                </StyledButton>
            </div>
        </div>
    );
};

export default PickResourceStep;
